import { useEffect } from 'react';
import { useWebSocketContext } from '../ws/WebsocketContext';
import { MessageType, WsMessage } from '../misc/domain/ws';
import { RoomId } from '../misc/domain';
import { useAppDispatch } from '../app/hooks';
import { resetSubtitles } from '../features/subtitles/subtitlesSlice';

/**
 * Hook for users to subscribe to subtitle channel for a room.
 * Subtitles are received via WebSocket and handled by the subtitles slice.
 *
 * @param roomId - The room ID to subscribe to for subtitles
 * @returns Object with subscription status
 */
export const useSubtitleSubscription = (roomId: RoomId | null) => {
  const { connected, sendMessage } = useWebSocketContext();
  const dispatch = useAppDispatch();

  // We are subscribed exactly while the subscribe effect below is active
  const subscribed = !!roomId && !!connected;

  useEffect(() => {
    if (!roomId || !connected) return;

    const channel = `/room/${roomId}/subtitles`;
    console.log('[Subtitles] Subscribing to channel:', channel);
    sendMessage({ kind: MessageType.SUBSCRIBE, channel } as WsMessage<void>);

    return () => {
      // On a disconnect-triggered cleanup the socket is already closed and
      // sendMessage drops the frame; the server clears subscriptions of
      // dropped connections itself.
      console.log('[Subtitles] Unsubscribing from channel:', channel);
      sendMessage({ kind: MessageType.UNSUBSCRIBE, channel } as WsMessage<void>);
      dispatch(resetSubtitles());
    };
  }, [roomId, connected, sendMessage, dispatch]);

  return { subscribed, connected };
};

export default useSubtitleSubscription;
